import { Menu, MenuItem, styled } from '@mui/material';
import BlockWrapper from 'Components/_NewElements/BlockWrapper';
import Icon from 'Components/_NewElements/Icon';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import CircularProgressLocal from 'Components/CircularProgress';
import ConfirmationDialog from 'Components/ConfirmationDialog';
import { Container, Item } from 'Components/Grid';
import TextArea from 'Components/InputHandler/components/TextArea';
import { hasPatientData, HeaderTimeDisplay, isThread } from 'Components/Messaging/components';
import ChatView from 'Components/Messaging/components/ChatView';
import Paper from 'Components/Paper';
import ToolTip from 'Components/ToolTip';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'Store/index';
import { actions as messagingActions } from 'Store/messaging';
import { messageFetchBuilder } from 'Store/messaging/fetchers/messages';
import { threadFetchBuilder } from 'Store/messaging/fetchers/threads';
import { topicFetchBuilder } from 'Store/messaging/fetchers/topics';
import { actions as sessionActions } from 'Store/session';
import { blurActiveElement } from 'Utility/domFocus';
import colors from '../../config/theme/colors';
import { AttachmentForm } from '../_NewElements/GeneralForms/AttachmentsForm';
import NewMessage from './NewMessage';

import type { IMessageListItem, IThreadListItem } from 'Store/messaging/interfaces';
import type { TTOPIC } from 'Store/messaging/types/TTopic';

const StyledUserCircle = styled('div')({
  backgroundColor: colors.darkGray,
  borderRadius: '50rem',
  width: '3rem',
  height: '3rem',
  color: colors.white,
  letterSpacing: '0.1rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

// const validateFile = (file: File) => {
//   const imageFileTypes = ['image/apng', 'image/bmp', 'image/gif', 'image/jpeg', 'image/pjpeg', 'image/png'];

//   return imageFileTypes.includes(file.type);
// };

const ThreadOpen = ({ thread }: IThreadOpen) => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });
  const [loading, setLoading] = React.useState<boolean>(false);

  const minimized = useAppSelector(s => s.session.messagingSessionSettings.minimized);

  const [messages, setMessages] = React.useState<IMessageListItem[]>([]);

  const updateMessages = () => {
    messageFetchBuilder<'list'>({ name: 'list', queryParams: [thread.patientId, thread.id] }).then(res => {
      if (Array.isArray(res)) {
        setMessages(res);
      }
    });
  };

  React.useEffect(() => {
    // Fetch messages when opening the thread
    updateMessages();
  }, []);

  const [newMessageBody, setNewMessageBody] = React.useState<string | null>(null);

  const sendNewMessage = () => {
    if (newMessageBody || acceptedAttachments.length > 0) {
      setLoading(true);
      messagingActions.newMessageToThread(thread.patientId, thread, newMessageBody, acceptedAttachments).then(() => {
        updateMessages();
        setNewMessageBody(null);
        setAcceptedAttachments([]);
        setLoading(false);
      });
    }
  };

  const ended = thread.completionTimestamp;

  // Attachments that have been inputted through the attachments form, ready to be sent
  const [acceptedAttachments, setAcceptedAttachments] = React.useState<File[]>([]);

  // Temporary attachments used for handling the attachment form
  const [tempAttachments, setTempAttachments] = React.useState<File[] | undefined>(undefined);
  const handleTempFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setTempAttachments(prev => {
        if (e.target.files?.[0]) return [...(prev ?? []), e.target.files[0]];
        return prev;
      });
    }
  };

  return (
    <>
      {messages.length > 0 && (
        <ChatView
          messages={messages}
          thread={thread}
          minimized={minimized}
          updateMessages={updateMessages}
          ended={ended}
        />
      )}
      {messages.length === 0 && <div />}
      {!ended && (
        <>
          {acceptedAttachments.length > 0 ? (
            <div style={{ marginTop: '2rem' }}>
              <div style={{ fontWeight: 600 }}>{fm('messaging.unsentAttachments')}</div>
              {acceptedAttachments.map(file => (
                <Container key={file.name}>
                  <Item>{file.name}</Item>
                  <Item sx={{ marginLeft: '1rem', cursor: 'pointer' }}>
                    <Icon
                      icon="close"
                      color="errorDefault"
                      button
                      onClick={() => {
                        const filteredAttachments = acceptedAttachments.filter(attch => attch.name !== file.name);
                        setAcceptedAttachments(filteredAttachments);
                      }}
                    />
                  </Item>
                </Container>
              ))}
            </div>
          ) : null}
          <Container style={{ marginTop: '2rem' }}>
            <Item xs={true}>
              <TextArea
                editing
                type="TextArea"
                name="reply"
                placeholder={fm('messaging.replyPlaceholder')}
                rows={2}
                fullWidth
                id="messagingReplyBox"
                autoHeight
                value={newMessageBody ?? ''}
                onChange={values => {
                  const newMessageString = values['reply'];
                  if (typeof newMessageString === 'string') {
                    setNewMessageBody(newMessageString);
                  }
                }}
              />
            </Item>
            <ToolTip
              hover
              title={fm('messaging.addAttachment')}
              content={
                <Item
                  xs={'auto'}
                  style={{ padding: '0 1rem', cursor: 'pointer' }}
                  onClick={() => setTempAttachments([])}
                >
                  <Icon icon="attach" />
                </Item>
              }
            />
            <Item xs={'auto'}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <ActionButtonRounded
                  width="auto"
                  filled
                  height={3}
                  fontSize={15}
                  text={'messaging.send'}
                  disabled={!newMessageBody && acceptedAttachments.length === 0}
                  loading={loading}
                  onClick={() => sendNewMessage()}
                />
              </div>
            </Item>
          </Container>
        </>
      )}
      <ConfirmationDialog
        open={!!tempAttachments}
        fullWidth={false}
        text={
          <div style={{ minWidth: '40rem' }}>
            {tempAttachments && (
              <AttachmentForm fm={fm} attachmentData={tempAttachments} handleFileChange={handleTempFileChange} />
            )}
          </div>
        }
        cancel={{
          callback: e => {
            e.stopPropagation();
            setTempAttachments(undefined);
          },
        }}
        confirm={{
          disabled: !(tempAttachments && typeof tempAttachments === 'object'),
          text: 'general.accept',
          callback: async e => {
            e.stopPropagation();
            // Add temp attachments to accepted attachments data
            tempAttachments && setAcceptedAttachments([...acceptedAttachments, ...tempAttachments]);
            setTempAttachments(undefined);
          },
        }}
      />
    </>
  );
};

interface IThreadOpen {
  thread: IThreadListItem;
}

const ThreadHeader = ({ thr, fm }: { thr: IThreadListItem; fm: (s: string) => string }) => {
  const lastDate = thr.latestMessage?.timestamp;

  const thisUserId: string = useAppSelector(s => s.session.data?.useruuid) ?? '';
  const dispatch = useAppDispatch();

  const thisUserIsTheHandler = thr.assignedFor?.id === thisUserId;

  const [anchor, setAnchor] = React.useState<null | Element>(null);
  const toggleMenu = (e: React.MouseEvent<Element>) => {
    e.stopPropagation();
    if (anchor) blurActiveElement();
    setAnchor(!anchor ? e.currentTarget : null);
  };

  const assignUserToggle = async (e: React.MouseEvent<Element>) => {
    e.stopPropagation();
    if (thisUserIsTheHandler) {
      await threadFetchBuilder({ name: 'unassign', queryParams: [thr.id] });
    } else {
      await threadFetchBuilder({ name: 'assign', queryParams: [thr.id, thisUserId] });
    }
    dispatch(messagingActions.loadThreadList(true));
    toggleMenu(e);
  };

  const completeDiscussionToggle = async (e: React.MouseEvent<Element>) => {
    e.stopPropagation();
    if (thr.completionTimestamp) {
      await threadFetchBuilder({ name: 'uncomplete', queryParams: [thr.id] });
    } else {
      await threadFetchBuilder({ name: 'complete', queryParams: [thr.id] });
    }
    dispatch(messagingActions.loadThreadList(true));
    toggleMenu(e);
  };

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = React.useState<boolean>(false);
  const deleteDiscussion = async (e: React.MouseEvent<Element>) => {
    e.stopPropagation();
    setDeleteConfirmationOpen(true);
    toggleMenu(e);
  };

  const completed = thr.completionTimestamp;

  return (
    <>
      <Container>
        <Item xs={true}>
          <div style={{ fontSize: '1.8rem' }}>{thr.topicTitle || fm('messaging.noTitle')}</div>
          {lastDate ? (
            <HeaderTimeDisplay date={lastDate} color={completed ? 'gray' : undefined} />
          ) : (
            <div style={{ fontSize: '1.4rem' }}>{fm('messaging.noDate')}</div>
          )}
        </Item>
        {thr.assignedFor && (
          <Item style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '1rem' }}>
            <ToolTip
              hover
              title={
                <>
                  {fm('messaging.handler')}: {`${thr.assignedFor.firstNames} ${thr.assignedFor.lastNames}`}
                </>
              }
              content={
                <StyledUserCircle>
                  <h2 style={{ margin: 0, padding: 0, fontSize: '1.6rem' }}>
                    {thr.assignedFor.firstNames.charAt(0)}
                    {thr.assignedFor.lastNames.charAt(0)}
                  </h2>
                </StyledUserCircle>
              }
            />
          </Item>
        )}
        <Item style={{ display: 'flex', alignItems: 'center' }} onClick={toggleMenu}>
          <Icon icon="moreVert" color={completed ? 'grayDark' : undefined} />
        </Item>
      </Container>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={toggleMenu}>
        <MenuItem onClick={assignUserToggle}>
          {fm(thisUserIsTheHandler ? 'messaging.unassignMeAsHandler' : 'messaging.assignMeAsHandler')}
        </MenuItem>
        <MenuItem onClick={completeDiscussionToggle}>
          {fm(thr.completionTimestamp ? 'messaging.markUncomplete' : 'messaging.markComplete')}
        </MenuItem>
        <MenuItem onClick={deleteDiscussion}>{fm('messaging.deleteDiscussion')}</MenuItem>
      </Menu>
      <ConfirmationDialog
        open={deleteConfirmationOpen}
        text={fm('messaging.deleteDiscussionConfirmation')}
        cancel={{
          callback: e => {
            e.stopPropagation();
            setDeleteConfirmationOpen(false);
          },
        }}
        confirm={{
          callback: async e => {
            e.stopPropagation();
            //await threadFetchBuilder({ name: 'delete', queryParams: [thr.id] });
            await topicFetchBuilder({ name: 'delete', queryParams: [thr.topicId] });
            dispatch(messagingActions.loadThreadList(true));
            setDeleteConfirmationOpen(false);
          },
        }}
      />
    </>
  );
};

const TopicBody = ({ topic }: { topic: TTOPIC }) => {
  const [topicData, setTopicData] = React.useState<TTOPIC | null>(null);
  const getTopicData = () => {
    const firstPatientId = typeof topic.targetPatients[0] === 'string' && topic.targetPatients[0];
    firstPatientId &&
      topicFetchBuilder<'fetch'>({ name: 'fetch', queryParams: [firstPatientId, topic.id] }).then(res => {
        if (res) {
          setTopicData(res);
        }
      });
  };
  React.useEffect(() => {
    getTopicData();
  }, []);

  return <div>{topicData?.body}</div>;
};

const MessasingToPatient = () => {
  const [openThreadId, setOpenThreadId] = React.useState<string | null>(null);

  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const dispatch = useAppDispatch();
  const minimized = useAppSelector(s => s.session.messagingSessionSettings.minimized);
  const position = useAppSelector(s => s.session.messagingSessionSettings.position);
  const newMessage = useAppSelector(s => s.session.messagingSessionSettings.newMessage);
  const selectedPlatform = useAppSelector(s => s.session.platforms?.selected);

  // Bit of a trick to unrender these tooltips when position or size is changed
  const [minimizeTooltip, setMinimizeTooltip] = React.useState<string | null>(
    fm(minimized ? 'messaging.maximize' : 'messaging.minimize'),
  );
  const [positionTooltip, setPositionTooltip] = React.useState<string | null>(
    fm(position === 'right' ? 'messaging.moveLeft' : 'messaging.moveRight'),
  );

  const setMinimize = () => {
    dispatch(sessionActions.setMessagesSessionSettings({ minimized: !minimized }));
    setMinimizeTooltip(null);
    setTimeout(() => {
      setMinimizeTooltip(fm(!minimized ? 'messaging.maximize' : 'messaging.minimize'));
    }, 100);
  };
  const setPosition = () => {
    const currentPosition = position ?? 'left';
    dispatch(sessionActions.setMessagesSessionSettings({ position: currentPosition === 'right' ? 'left' : 'right' }));
    setPositionTooltip(null);
    setTimeout(() => {
      setPositionTooltip(fm(currentPosition === 'left' ? 'messaging.moveLeft' : 'messaging.moveRight'));
    }, 100);
  };

  const closeMessaging = () => dispatch(sessionActions.setMessagesSessionSettings({ open: false }));

  const newDiscussionChange = (newMessage: Record<'title' | 'body', string> | undefined) => {
    dispatch(sessionActions.setMessagesSessionSettings({ newMessage }));
  };

  const postNewDiscussion = (attachments?: File[]) => {
    if (newMessage?.title && newMessage.body && selectedPlatform) {
      newDiscussionChange(undefined);
      setLoadingThreads(true);

      dispatch(
        messagingActions.createNewDiscussionWithPatient(
          newMessage.title,
          newMessage.body,
          [selectedPlatform],
          attachments,
        ),
      );
      setTimeout(() => {
        setLoadingThreads(false);
      }, 700);
    }
  };

  const [loadingThreads, setLoadingThreads] = React.useState<boolean>(true);
  React.useEffect(() => {
    const loadingPromises = [dispatch(messagingActions.loadTopics()), dispatch(messagingActions.loadThreadList(true))];

    Promise.all(loadingPromises).then(() => {
      setLoadingThreads(false);
    });
  }, []);

  const threads = useAppSelector(s => s.messaging.threads) ?? [];

  const patientId = useAppSelector(s => s.session.data?.patientid);
  const topics = useAppSelector(s => s.messaging.topics);
  const topicsForPatient = [...(topics ?? [])].filter(to => {
    if (to.targetPatients.length < 2) return false;
    if (hasPatientData(to.targetPatients)) {
      const ids = to.targetPatients.map(targ => targ.id);
      return patientId && ids.includes(patientId);
    } else {
      return patientId && to.targetPatients.includes(patientId);
    }
  });

  const activePatientItems = [...threads, ...topicsForPatient]
    .filter(item => !item.deletionTimestamp)
    .filter(item => (isThread(item) ? !item.completionTimestamp : !item.closureTimestamp))
    .toSorted((a, b) => {
      const aDate = isThread(a) ? (a.latestMessage?.timestamp ?? Date.now()) : a.creationTimestamp;
      const bDate = isThread(b) ? (b.latestMessage?.timestamp ?? Date.now()) : b.creationTimestamp;
      return bDate - aDate;
    });

  const endedPatientItems = [...threads, ...topicsForPatient]
    .filter(item => !item.deletionTimestamp)
    .filter(item => (isThread(item) ? item.completionTimestamp : item.closureTimestamp))
    .toSorted((a, b) => {
      const aDate = isThread(a) ? (a.latestMessage?.timestamp ?? Date.now()) : a.creationTimestamp;
      const bDate = isThread(b) ? (b.latestMessage?.timestamp ?? Date.now()) : b.creationTimestamp;
      return bDate - aDate;
    });

  return (
    <div
      style={{
        backgroundColor: 'white',
        height: minimized ? '30rem' : 'calc(100% - 2rem)',
        position: 'fixed',
        bottom: '1rem',
        left: !position || position === 'left' ? '1rem' : undefined,
        right: position === 'right' ? '1rem' : undefined,
        width: minimized ? '65rem' : '65rem',
        zIndex: 50,
      }}
    >
      <Paper
        padding={0}
        elevation={15}
        square
        style={{ height: '100%', overflowY: 'auto', padding: '0 1.5rem 1.5rem 1.5rem' }}
      >
        <div
          style={{
            padding: '1rem 0 0 0',
            height: '3.5rem',
            position: 'sticky',
            top: 0,
            backgroundColor: 'white',
            zIndex: 10,
          }}
        >
          <Container>
            <Item xs={true}>
              <div style={{ fontSize: '1.8rem', fontWeight: 600, color: colors.primary }}>
                {fm(newMessage ? 'messaging.newDiscussion' : 'messaging.messagingPatient')}
              </div>
            </Item>

            {/* <Item style={{ display: 'flex', alignItems: 'center', marginRight: '2rem' }}>
              <div style={{ marginRight: '0.3rem' }}>{fm('messaging.showClosedShort')}</div>
              <Checkbox style={{ padding: 0 }} />
            </Item> */}
            <Item xs={'auto'} style={{ marginRight: '1rem' }}>
              <ActionButtonRounded
                text={<>{<Icon icon={position === 'right' ? 'leftMoveArrow' : 'rightMoveArrow'} size={2} />}</>}
                height={2.5}
                width={'auto'}
                fontSize={14}
                onClick={() => setPosition()}
                customButtonStyles={{ padding: '0 0.5rem' }}
                tooltip={positionTooltip ? <>{positionTooltip}</> : undefined}
              />
            </Item>
            <Item xs={'auto'} style={{ marginRight: '1rem' }}>
              <ActionButtonRounded
                text={<>{<Icon icon={minimized ? 'maximize' : 'minimize'} size={1.8} />}</>}
                height={2.5}
                width={'auto'}
                fontSize={14}
                onClick={() => setMinimize()}
                customButtonStyles={{ padding: '0 0.5rem' }}
                tooltip={minimizeTooltip ? <>{minimizeTooltip}</> : undefined}
              />
            </Item>
            {!newMessage && (
              <Item xs={'auto'} style={{ marginRight: '1rem' }}>
                <ActionButtonRounded
                  text={'messaging.newDiscussion'}
                  height={2.5}
                  width="auto"
                  fontSize={14}
                  filled
                  onClick={() => newDiscussionChange({ title: '', body: '' })}
                />
              </Item>
            )}
            <Item xs={'auto'}>
              <ActionButtonRounded
                text="general.close"
                height={2.5}
                width="auto"
                fontSize={14}
                filled
                onClick={() => closeMessaging()}
              />
            </Item>
          </Container>
        </div>

        {newMessage ? (
          <NewMessage postNewDiscussion={postNewDiscussion} />
        ) : loadingThreads ? (
          <Container justifyContent={'center'}>
            <Item style={{ paddingTop: '1rem' }}>
              <CircularProgressLocal />
            </Item>
          </Container>
        ) : activePatientItems.length === 0 && endedPatientItems.length === 0 ? (
          <Container justifyContent={'center'}>
            <Item style={{ paddingTop: '1rem' }}>
              <h3>{fm('messaging.noDiscussions')}</h3>
            </Item>
          </Container>
        ) : (
          <>
            {activePatientItems.map(item => (
              <div key={item.id} style={{ marginBottom: '1rem' }}>
                {isThread(item) ? (
                  <BlockWrapper
                    collapse={{
                      open: openThreadId === item.id,
                      collapseHandler: () => {
                        setOpenThreadId(openThreadId === item.id ? null : item.id);
                      },
                    }}
                    color="light"
                    title={<ThreadHeader thr={item} fm={fm} />}
                    contentMargin={1}
                    compact
                  >
                    <ThreadOpen thread={item} />
                  </BlockWrapper>
                ) : (
                  <BlockWrapper
                    collapse={{
                      open: openThreadId === item.id,
                      collapseHandler: () => {
                        setOpenThreadId(openThreadId === item.id ? null : item.id);
                      },
                    }}
                    color="warning"
                    title={
                      <div style={{ fontSize: '1.8rem' }} className={openThreadId === item.id ? '' : 'ellipsis'}>
                        <Container>
                          <Item style={{ width: '48rem' }} className={openThreadId === item.id ? '' : 'ellipsis'}>
                            {item.title}
                          </Item>
                          <Item xs={true} style={{ fontSize: '1.4rem', textAlign: 'end' }}>
                            {fm('messaging.groupMessage')}
                          </Item>
                        </Container>
                        {openThreadId === item.id && (
                          <HeaderTimeDisplay date={item.creationTimestamp} color={'yellow'} />
                        )}
                      </div>
                    }
                    contentMargin={1}
                    compact
                  >
                    <TopicBody topic={item} />
                  </BlockWrapper>
                )}
              </div>
            ))}

            {endedPatientItems.length > 0 && (
              <div>
                <h3 style={{ color: colors.primary, margin: '2rem 0 1rem 0' }}>
                  {fm('messaging.completedDiscussions')}
                </h3>
                {endedPatientItems.map(item => (
                  <div key={item.id} style={{ marginBottom: '1rem' }}>
                    {isThread(item) ? (
                      <BlockWrapper
                        collapse={{
                          open: openThreadId === item.id,
                          collapseHandler: () => {
                            setOpenThreadId(openThreadId === item.id ? null : item.id);
                          },
                        }}
                        color="gray"
                        title={<ThreadHeader thr={item} fm={fm} />}
                        contentMargin={1}
                        compact
                      >
                        <ThreadOpen thread={item} />
                      </BlockWrapper>
                    ) : (
                      <BlockWrapper
                        collapse={{
                          open: openThreadId === item.id,
                          collapseHandler: () => {
                            setOpenThreadId(openThreadId === item.id ? null : item.id);
                          },
                        }}
                        color="gray"
                        title={
                          <div
                            style={{ width: '55rem', fontSize: '1.8rem' }}
                            className={openThreadId === item.id ? '' : 'ellipsis'}
                          >
                            {item.title}
                            {openThreadId === item.id && (
                              <HeaderTimeDisplay date={item.creationTimestamp} color={'yellow'} />
                            )}
                          </div>
                        }
                        contentMargin={1}
                        compact
                      >
                        <TopicBody topic={item} />
                      </BlockWrapper>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Paper>
    </div>
  );
};

export default MessasingToPatient;
